import { yupResolver } from '@hookform/resolvers/yup'
import { map } from 'lodash'
import { useEffect, useState } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { Link, LinkPayload } from '../../../common/models'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import {
  deleteSettingsLink,
  getSettingsLinks,
  updateSettingsLinks,
} from '../../../core/services/festival-settings'
import { useAppSelector } from '../../../store'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { Header, PageTitle } from '@/components'
import FormGroup from '@/core/components/FormGroup'
import { Button, Card, Input } from '@/components/ui'
import { Icon } from '@/common/components/Icon'

interface ILinksValues {
  links?: LinkPayload[]
}

export const LinksPage: React.FC = () => {
  const { editionId, festivalName } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name

  const [links, setLinks] = useState<Link[]>([])
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const [removeLink, setRemoveLink] = useState<number>(0)

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const { hasEditPermission } = useCheckPermission({ page: 'SETTINGS' })

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const response = await getSettingsLinks()

        setLinks(response.data)
        setValue('links', response.data)
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName])

  const deleteFunc = () => {
    // prettier-ignore
    (async () => {
      remove(removeLink)
      if (links[removeLink].id) {
        try {
          const { status } = await deleteSettingsLink(links[removeLink].id)
          const response = await getSettingsLinks()

          setLinks(response.data)
          setValue('links', response.data)
          if (status === 204 && response.status === 200) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Delete Successful')
          }
        } catch (e) {
          if (e instanceof Error) {
            setIsError(true)
            setShowMessageBox(true)
            setMessage(e.message)
            setErrorStatus((e as { response?: { status?: number } })?.response?.status)
          } else {
            console.error(e)
          }
        }
      }
    })()
  }

  const schema = yup.object().shape({
    links: yup
      .array()
      .min(1)
      .of(
        yup.object().shape({
          id: yup.string(),
          categoryName: yup.string(),
          deepLink: yup.string(),
        }),
      )
      .required(),
  })
  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    control,
  } = useForm<ILinksValues>({
    mode: 'onChange',
    resolver: yupResolver<ILinksValues>(schema),
    defaultValues: {
      links: [
        {
          id: '',
          categoryName: '',
          deepLink: '',
        },
      ],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'links', // unique name for your Field Array
  })

  const submit = async (values: ILinksValues) => {
    const data: LinkPayload[] = values.links || []

    try {
      const { status } = await updateSettingsLinks(data)
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const handleCancel = async () => {
    setValue('links', links)
  }

  return (
    <>
      <Header breadcrumbs={['Settings', 'Notifications', 'Links']} />
      <Card className="p-7.5 bg-gradient-4 border border-monochrome-120 max-h-[calc(100vh-118px)] overflow-y-auto space-y-4">
        <form id="settings-links-form" onSubmit={handleSubmit(submit)}>
          <h3 className="text-title-xs text-monochrome-1 mb-6">Links</h3>
          <div className="flex flex-col items-start gap-3 w-full">
            {map(fields, (field, index) => {
              return (
                <div key={field.id} className="flex flex-col w-full">
                  <div className="flex items-end gap-3" key={field.id}>
                    <FormGroup label="Category Name">
                      <Input
                        type="text"
                        isInvalid={
                          !!errors?.links?.[index]?.categoryName?.message
                        }
                        {...register(`links.${index}.categoryName`)}
                        placeholder="Enter Category Name"
                        disabled={!hasEditPermission}
                      />
                    </FormGroup>
                    <FormGroup label="Deep Link">
                      <Input
                        type="text"
                        isInvalid={!!errors?.links?.[index]?.deepLink?.message}
                        {...register(`links.${index}.deepLink`)}
                        placeholder="Enter Deep Link"
                        disabled={!hasEditPermission}
                      />
                    </FormGroup>
                    {fields.length > 1 && (
                      <Button
                        variant="destructive"
                        type="button"
                        onClick={() => {
                          setDeleteConfirmationModal(true)
                          setRemoveLink(index)
                        }}
                        disabled={!hasEditPermission}
                      >
                        Delete
                        <Icon
                          icon="close-circle"
                          width="18"
                          height="18"
                          viewBox="0 0 18 18"
                          fill="none"
                          className="shrink-0"
                        />
                      </Button>
                    )}
                  </div>
                </div>
              )
            })}
            <div className="flex justify-center items-center w-full">
              <Button
                variant="secondary"
                type="button"
                className="bg-monochrome-120 text-monochrome-1 border"
                onClick={() =>
                  append({
                    categoryName: '',
                    deepLink: '',
                  })
                }
                disabled={!hasEditPermission}
              >
                Add New
                <Icon
                  icon="plus"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  className="shrink-0"
                />
              </Button>
            </div>
          </div>
        </form>

        <div className="flex justify-end gap-2 pt-4 border-t border-monochrome-120 [&>*]:w-[154px]">
          <Button
            onClick={handleCancel}
            variant="secondary"
            data-dismiss="modal"
            disabled={!hasEditPermission}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            form="settings-links-form"
            type="submit"
            disabled={!hasEditPermission}
          >
            Save
          </Button>
        </div>
      </Card>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Link'}
        deleteFunc={deleteFunc}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
